import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import type {
  CanvasNode,
  QuizData,
  ConceptData,
  NoteData,
  SummaryData,
  ImageData,
} from '@/shared/types';
import { QuizInteraction } from '@/features/quiz/QuizInteraction';
import { SummaryQuizInteraction } from '@/features/quiz/SummaryQuizInteraction';
import { ImageNode } from './nodes/ImageNode';
import { useNotebookStore } from '@/shared/stores/notebookStore';
import { useSettingsStore } from '@/shared/stores/settingsStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { ttsManager } from '@/lib/llm/ttsManager';
import { useTypingAnimation } from './useTypingAnimation';
import { useMediaQuery } from '@/shared/useMediaQuery';
import {
  Play,
  Pause,
  Square,
  List,
  Plus,
  Download,
  Volume2,
  VolumeX,
  Monitor,
  Moon,
  Sun,
} from 'lucide-react';
import { exportSessionJson } from '@/lib/export/json';
import { downloadSessionMarkdown } from '@/lib/export/markdown';
import { getNextLearningAction, normalizeLearningProgress } from '@/shared/learningProgress';
import { AccessibleDialog } from '@/lib/components/AccessibleDialog';
import styles from './MobileFocusView.module.css';

interface Props {
  nodes: CanvasNode[];
  progress?: { stage: string; label: string };
  isGenerating?: boolean;
  onHome?: () => void;
  onAddNote?: () => void;
}

function formatKind(node: CanvasNode): string {
  const d = node.data;
  if (d.kind === 'concept') return 'Concept';
  if (d.kind === 'quiz') {
    return d.format
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
  }
  if (d.kind === 'note') return 'Note';
  if (d.kind === 'image') return 'Diagram';
  if (d.kind === 'summary') return 'Summary';
  return 'Node';
}

function renderContent(node: CanvasNode): { title?: string; body: string } {
  const d = node.data;
  if (d.kind === 'concept') {
    return { title: d.title, body: d.explanation + '\n\n' + d.example };
  }
  if (d.kind === 'quiz') {
    const statusLine =
      d.attempts.length > 0 ? 'Attempts: ' + d.attempts.length + ' \u00b7 ' + d.state : '';
    return { title: d.prompt, body: statusLine };
  }
  if (d.kind === 'note') {
    return { body: d.text };
  }
  if (d.kind === 'image') {
    return { body: '' };
  }
  if (d.kind === 'summary') {
    return { title: d.recap.length + ' recap points', body: d.recap.join('\n') };
  }
  return { body: '' };
}

export function MobileFocusView({
  nodes,
  progress,
  isGenerating = false,
  onHome,
  onAddNote,
}: Props) {
  const [index, setIndex] = useState(0);
  const [showOutline, setShowOutline] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState<{
    quizId: string;
    quiz: QuizData;
    conceptTitle: string;
  } | null>(null);
  const [summaryQuiz, setSummaryQuiz] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [caption, setCaption] = useState('');
  const [captionVisible, setCaptionVisible] = useState(false);
  const [captionAnnouncement, setCaptionAnnouncement] = useState('');
  const cardRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, [index, nodes.length, isGenerating]);

  // Clamp index when nodes shrink
  useEffect(() => {
    if (nodes.length === 0) {
      setIndex(0);
    } else {
      setIndex((i) => Math.min(i, nodes.length - 1));
    }
  }, [nodes.length]);

  // Reset scroll position when navigating to a new node
  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.scrollTop = 0;
    }
  }, [index]);

  const node = nodes[index];
  const total = nodes.length;

  const notebookMode = useNotebookStore((s) => s.notebookMode);
  const ttsPlaying = useNotebookStore((s) => s.ttsPlaying);
  const ttsPaused = useNotebookStore((s) => s.ttsPaused);
  const segmentIndex = useNotebookStore((s) => s.segmentIndex);
  const totalSegments = useNotebookStore((s) => s.totalSegments);

  const ttsEnabled = useSettingsStore((s) => s.ttsEnabled);
  const ttsRate = useSettingsStore((s) => s.ttsRate);
  const setTtsEnabled = useSettingsStore((s) => s.setTtsEnabled);
  const setTtsRate = useSettingsStore((s) => s.setTtsRate);
  const theme = useSettingsStore((s) => s.theme);
  const setTheme = useSettingsStore((s) => s.setTheme);
  const session = useSessionStore((state) =>
    state.currentId ? (state.sessions.find((item) => item.id === state.currentId) ?? null) : null,
  );
  const updateCurrent = useSessionStore((state) => state.updateCurrent);

  const handlePlayPause = useCallback(() => {
    if (ttsPaused) {
      ttsManager.resume();
    } else if (!ttsPlaying) {
      if (node && ttsEnabled) {
        ttsManager.setRate(ttsRate);
        if (node.data.kind === 'concept') {
          const text = node.data.title + '. ' + node.data.explanation;
          ttsManager.enqueue({ nodeId: node.id, text });
        } else if (node.data.kind === 'summary') {
          const text = node.data.recap.join('. ');
          ttsManager.enqueue({ nodeId: node.id, text });
        }
      }
      ttsManager.start();
    } else {
      ttsManager.pause();
    }
  }, [ttsPlaying, ttsPaused, node, ttsEnabled, ttsRate]);

  const handleStopTts = useCallback(() => {
    ttsManager.stop();
  }, []);

  // Auto-TTS on card change in notebook mode, with dedup check.
  // Gated on !prefers-reduced-motion AND the user's TTS-enabled setting to
  // match the desktop notebook behavior.
  useEffect(() => {
    if (!notebookMode || !node || prefersReducedMotion || !ttsEnabled) return;
    if (node.data.kind === 'concept') {
      if (ttsManager.hasSegment(node.id)) return;
      // Don't enqueue shell text (outline blurb): if we did, the hasSegment
      // dedup would permanently block the real explanation once it lands.
      // Mirrors the desktop auto-enqueue guard in CanvasPage.
      if ((node.data as ConceptData).example === 'Loading...') return;
      const text = node.data.title + '. ' + node.data.explanation;
      ttsManager.enqueue({ nodeId: node.id, text });
    } else if (node.data.kind === 'summary') {
      if (ttsManager.hasSegment(node.id)) return;
      const text = node.data.recap.join('. ');
      ttsManager.enqueue({ nodeId: node.id, text });
    } else {
      return;
    }
    if (!ttsManager.isPlaying && !ttsManager.isPaused) {
      ttsManager.start();
    }
  }, [notebookMode, node, prefersReducedMotion, ttsEnabled]);

  useEffect(() => {
    if (!notebookMode) {
      setCaption('');
      setCaptionVisible(false);
      setCaptionAnnouncement('');
      return;
    }

    const subId = ttsManager.subscribe('__caption__', {
      onSegmentStart: () => {
        setCaptionVisible(true);
        setCaptionAnnouncement('Narration started.');
      },
      onCharProgress: (_nodeId, _charIndex, text?: string) => {
        if (text != null) {
          setCaption(text);
          setCaptionVisible(true);
        }
      },
      onSegmentEnd: () => {
        setCaptionVisible(false);
        setCaptionAnnouncement('Narration finished.');
      },
    });

    return () => {
      ttsManager.unsubscribe(subId);
      setCaptionVisible(false);
      setCaptionAnnouncement('');
    };
  }, [notebookMode]);

  const conceptTitles = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of nodes) {
      if (n.data.kind === 'concept') {
        map.set(n.id, (n.data as ConceptData).title);
      }
    }
    return map;
  }, [nodes]);

  const kindLabel = useMemo(() => (node ? formatKind(node) : ''), [node]);
  const { title, body } = useMemo(() => (node ? renderContent(node) : { body: '' }), [node]);

  // Typewriter reveal for concept/summary prose, mirroring the desktop notebook.
  const typing = useTypingAnimation(node?.id ?? '', body, false);
  const revealedBody =
    notebookMode && node && (node.data.kind === 'concept' || node.data.kind === 'summary')
      ? body.slice(0, typing.revealed)
      : body;

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setIndex((i) => Math.min(nodes.length - 1, i + 1)),
    [nodes.length],
  );

  const openQuiz = useCallback(() => {
    if (!node || node.data.kind !== 'quiz') return;
    const conceptTitle = conceptTitles.get(node.data.parentConceptId) ?? 'Concept';
    setActiveQuiz({ quizId: node.id, quiz: node.data, conceptTitle });
  }, [node, conceptTitles]);

  const closeQuiz = useCallback(() => {
    setActiveQuiz(null);
  }, []);

  const updateNote = useCallback(
    (noteId: string, text: string) => {
      if (!session) return;
      const updatedNodes = session.nodes.map((item) =>
        item.id === noteId && item.data.kind === 'note'
          ? { ...item, data: { ...item.data, text } as NoteData }
          : item,
      );
      updateCurrent({ nodes: updatedNodes });
    },
    [session, updateCurrent],
  );

  const cycleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'auto' : 'light');
  }, [theme, setTheme]);
  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;

  const conceptProgress = useMemo(() => {
    const conceptNodes = nodes.filter((item) => item.data.kind === 'concept');
    if (!node || conceptNodes.length === 0) return null;
    const conceptIndex =
      node.data.kind === 'concept'
        ? node.data.index
        : conceptNodes.findIndex((item) => {
            if (item.data.kind !== 'concept') return false;
            const parentId =
              node.data.kind === 'quiz'
                ? node.data.parentConceptId
                : node.data.kind === 'note'
                  ? node.data.linkedConceptId
                  : undefined;
            return parentId === item.id;
          });
    return conceptIndex >= 0 ? `Concept ${conceptIndex + 1} of ${conceptNodes.length}` : null;
  }, [node, nodes]);

  const navigationCue = useMemo(() => {
    const prevNode = index > 0 ? nodes[index - 1] : null;
    const nextNode = index < nodes.length - 1 ? nodes[index + 1] : null;
    const summarize = (item: CanvasNode | null) => {
      if (!item) return 'Start';
      const kind = formatKind(item).toLowerCase();
      return `${kind}${item.data.kind === 'concept' ? `: ${(item.data as ConceptData).title}` : ''}`;
    };
    return `Prev: ${summarize(prevNode)} · Next: ${summarize(nextNode)}`;
  }, [index, nodes]);

  const summaryData = node?.data.kind === 'summary' ? (node.data as SummaryData) : null;

  const lessonComplete = useMemo(() => {
    if (!session) return false;
    const conceptIds = session.nodes
      .filter((item) => item.data.kind === 'concept')
      .sort((a, b) => {
        const ai = a.data.kind === 'concept' ? a.data.index : 0;
        const bi = b.data.kind === 'concept' ? b.data.index : 0;
        return ai - bi;
      })
      .map((item) => item.id);
    if (conceptIds.length === 0) return false;
    const progressState = normalizeLearningProgress(
      session.lastConceptId,
      session.completedConceptIds,
      session.nextReviewAtByConceptId,
      session.lastActivityAt,
    );
    return getNextLearningAction(progressState, conceptIds).kind === 'complete';
  }, [session]);

  const isGeneratingProgress = isGenerating || (progress != null && progress.stage !== 'done');

  const outlineItemClass = (isCurrent: boolean) => {
    return [styles.outlineItem, isCurrent ? styles.activeOutlineItem : '']
      .filter(Boolean)
      .join(' ');
  };

  return (
    <div className={styles.wrapper} data-notebook={notebookMode ? 'true' : undefined}>
      {isGeneratingProgress && progress && (
        <div className={styles.progressBar}>
          <span className={styles.progressDot} />
          <span className={styles.progressLabel}>{progress.label}</span>
        </div>
      )}

      {isGenerating && node?.data.kind === 'concept' && (
        <div className={styles.streamingNotice} role="status">
          More sections are generating — review this one, then continue as they arrive.
        </div>
      )}

      {lessonComplete && !isGeneratingProgress && (
        <div className={styles.completionBanner} role="status">
          <strong>Lesson complete!</strong> Take the final quiz or review any concept from the
          outline.
        </div>
      )}

      <div className={styles.topActions}>
        {onHome && (
          <button className={styles.topActionBtn} onClick={onHome} type="button">
            <Plus size={14} />
            <span>New lesson</span>
          </button>
        )}
        <button
          className={styles.topActionBtn}
          onClick={() => setShowOutline((v) => !v)}
          aria-expanded={showOutline}
          aria-controls="mobile-outline"
          type="button"
        >
          <List size={14} />
          <span>Table of contents</span>
        </button>
        <button
          className={styles.topActionBtn}
          onClick={cycleTheme}
          aria-label={`Theme: ${theme}`}
          type="button"
        >
          <ThemeIcon size={14} />
          <span>Theme: {theme}</span>
        </button>
        {conceptProgress && <span className={styles.lessonProgress}>{conceptProgress}</span>}
      </div>

      <div className={styles.card} ref={cardRef} tabIndex={-1}>
        {node ? (
          <div className={styles.nodeContent}>
            <div className={styles.kindTag}>{kindLabel}</div>
            {title && <div className={styles.title}>{title}</div>}
            {revealedBody && (
              <div className={styles.body}>
                {revealedBody
                  .split(/\n+/)
                  .filter(Boolean)
                  .map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
              </div>
            )}
            {node.data.kind === 'image' && (
              <div className={styles.body}>
                <ImageNode id={node.id} data={node.data as ImageData} />
              </div>
            )}
            {node.data.kind === 'quiz' && (
              <button className={styles.answerBtn} onClick={openQuiz}>
                {(node.data as QuizData).attempts.length > 0 ? 'Answer again' : 'Answer quiz'}
              </button>
            )}
            {node.data.kind === 'summary' && (
              <button className={styles.answerBtn} onClick={() => setSummaryQuiz(true)}>
                {session && Object.keys(session.scores).length > 0
                  ? 'Review final results'
                  : 'Take final quiz'}
              </button>
            )}
            {node.data.kind === 'note' && (
              <label className={styles.noteEditor}>
                <span>Edit note</span>
                <textarea
                  value={(node.data as NoteData).text}
                  onChange={(event) => updateNote(node.id, event.target.value)}
                  rows={6}
                />
              </label>
            )}
          </div>
        ) : (
          <div className={styles.emptyCard}>No content to display</div>
        )}
      </div>

      {notebookMode && (
        <div className={styles.mobileTtsControls}>
          <button
            onClick={() => setTtsEnabled(!ttsEnabled)}
            title={ttsEnabled ? 'Disable narration' : 'Enable narration'}
            aria-label={ttsEnabled ? 'Disable narration' : 'Enable narration'}
            aria-pressed={ttsEnabled}
            type="button"
          >
            {ttsEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
          </button>
          <button
            onClick={handlePlayPause}
            className={styles.playPauseBtn}
            title={
              ttsPaused ? 'Resume narration' : ttsPlaying ? 'Pause narration' : 'Play narration'
            }
            aria-label={
              ttsPaused ? 'Resume narration' : ttsPlaying ? 'Pause narration' : 'Play narration'
            }
            type="button"
          >
            {ttsPaused ? <Play size={14} /> : ttsPlaying ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <button
            onClick={handleStopTts}
            className={styles.stopBtn}
            aria-label="Stop narration"
            disabled={!ttsPlaying && !ttsPaused}
            type="button"
            title="Stop"
          >
            <Square size={14} />
          </button>
          <span className={styles.mobileTtsLabel}>
            {totalSegments > 0 ? segmentIndex + 1 + ' / ' + totalSegments : 'Queued'}
          </span>
          <select
            value={ttsRate}
            onChange={(event) => setTtsRate(Number(event.target.value))}
            aria-label="Narration speed"
          >
            <option value={0.75}>0.75×</option>
            <option value={1}>1×</option>
            <option value={1.25}>1.25×</option>
            <option value={1.5}>1.5×</option>
            <option value={2}>2×</option>
          </select>
        </div>
      )}

      {notebookMode && (
        <div className={styles.mobilePacingBar}>
          <span className={styles.pacingCue}>{navigationCue}</span>
        </div>
      )}

      {notebookMode && captionVisible && caption && (
        <div className={styles.mobileCaption} aria-hidden="true">
          {caption}
        </div>
      )}

      {notebookMode && (
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {captionAnnouncement}
        </div>
      )}

      <div className={styles.mobileActions}>
        {onAddNote && (
          <button type="button" onClick={onAddNote}>
            <Plus size={14} /> Add note
          </button>
        )}
        <button type="button" onClick={() => setShowExport((value) => !value)}>
          <Download size={14} /> Export
        </button>
        {showExport && session && (
          <div className={styles.mobileExportMenu}>
            <button type="button" onClick={() => exportSessionJson(session)}>
              JSON
            </button>
            <button type="button" onClick={() => downloadSessionMarkdown(session)}>
              Markdown
            </button>
          </div>
        )}
      </div>

      <div className={styles.nav}>
        <button
          className={styles.navBtn}
          onClick={goPrev}
          disabled={index === 0 || total === 0}
          aria-label="Previous concept"
          title="Previous concept"
        >
          &lsaquo;
        </button>
        <span className={styles.counter}>{navigationCue}</span>
        <button
          className={styles.navBtn}
          onClick={goNext}
          disabled={index === total - 1 || total === 0}
          aria-label="Next concept"
          title="Next concept"
        >
          &rsaquo;
        </button>
      </div>

      {activeQuiz && (
        <QuizInteraction
          quiz={activeQuiz.quiz}
          quizId={activeQuiz.quizId}
          conceptTitle={activeQuiz.conceptTitle}
          onClose={closeQuiz}
          notebookMode={notebookMode}
        />
      )}

      {summaryQuiz && summaryData && session && (
        <SummaryQuizInteraction
          quizData={summaryData.finalQuiz}
          onClose={() => setSummaryQuiz(false)}
          onRetake={() => setSummaryQuiz(true)}
          initialScores={session.scores}
          onUpdateScores={(scores) => updateCurrent({ scores })}
        />
      )}

      {showOutline && (
        <AccessibleDialog
          label="Outline"
          onClose={() => setShowOutline(false)}
          overlayClassName={styles.outlineOverlay}
          panelClassName={styles.outlinePanel}
          initialFocusSelector=".closeOutlineBtn"
        >
          <div className={styles.outlineHeader}>
            <span className={styles.outlineHeaderTitle}>Outline</span>
            <button
              className={styles.closeOutlineBtn}
              onClick={() => setShowOutline(false)}
              aria-label="Close outline"
              type="button"
            >
              ✕
            </button>
          </div>
          <div className={styles.outlineList}>
            {nodes.map((n, i) => {
              const isCurrent = i === index;
              const kind = formatKind(n);
              const { title: nodeTitle } = renderContent(n);
              const displayTitle =
                nodeTitle || (n.data.kind === 'note' ? n.data.text.slice(0, 30) + '...' : kind);
              return (
                <button
                  key={n.id}
                  className={outlineItemClass(isCurrent)}
                  onClick={() => {
                    setIndex(i);
                    setShowOutline(false);
                  }}
                  type="button"
                >
                  <span className={styles.outlineKind}>{kind}</span>
                  <span className={styles.outlineTitle}>{displayTitle}</span>
                </button>
              );
            })}
          </div>
        </AccessibleDialog>
      )}
    </div>
  );
}
